import { useState, Fragment, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useKupStore from '~kup/store';
import { OrderItemState } from '~/pages/shop/types';
import Page from '~/components/Page';
import Container from '~/components/Container.tsx';
import Button from '~/components/Button.tsx';
import Checkbox, { BareCheckboxProps } from '~/components/Checkbox';
import Stack from '~/components/Stack.tsx';
import Typography from '~/components/Typography.tsx';
import useDelivery from '~/pages/shop/hooks/useDelivery.ts';
import Divider from '~/components/Divider';
import BasketItem from '~/pages/shop/basket/components/BasketItem.tsx';
import Area from '~/components/Area.tsx';
import { BasketProductItem } from '~kup/models/Basket.ts';
import ModifyItemPopover from '~/pages/shop/basket/components/ModifyItemPopover.tsx';
import RemoveItemsPopover from '~/pages/shop/basket/components/RemoveItemsPopover.tsx';
import Spotlight from '~/components/Spotlight.tsx';
import { useTranslation } from 'react-i18next';
import useBatchLogger from '~/hooks/useBatchLogger';
import RecommendedProducts from './components/RecommendedProducts';
import useSyncShopping from '~/hooks/useSyncShopping.ts';
import Icon from '~/components/Icon';
import useDeliveryCutOff from '~/pages/shop/hooks/useDeliveryCutOff.ts';
import FillBox from '~/components/FillBox.tsx';
import Logo from '~/components/Logo';
import LikeIconWithCount from '~/components/LikeIconWithCount.tsx';
import useCouponHelper from '~/hooks/useCouponHelper.ts';
import IssuableCouponsButton from '~/components/IssuableCouponsButton.tsx';

type ItemGroups = {
  productId: string;
  productName: string;
  items: BasketProductItem[];
};

export default function ShopBasketPage() {
  useSyncShopping();
  const { setAppliedCoupon } = useKupStore((state) => ({
    setAppliedCoupon: state.setAppliedCoupon,
  }));
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { logAction } = useBatchLogger();
  const { currency, basket, basketChecks, setBasketCheck, setBasketChecks } = useKupStore();
  const isAllChecked =
    Object.values(basketChecks).length > 0 &&
    Object.values(basketChecks).reduce((a, b) => a && b, true);

  const checkedItems = basket.items.filter((item) => basketChecks[item.key]);
  const checkedAmount = checkedItems.map((i) => i.amount).reduce((a, b) => a + b, 0);
  const checkedAvailableAmount = checkedItems
    .map((i) => i.availableAmount)
    .reduce((a, b) => a + b, 0);

  const { issuableCoupons, bestUsableCoupon, handleIssueCoupons } = useCouponHelper(
    checkedItems.map((item) => item.id ?? '').filter((s) => !!s)
  );

  const delivery = useDelivery(checkedAvailableAmount);
  const deliveryFee = checkedAvailableAmount > 0 ? delivery.fee : 0;
  const totalOrderAvailableAmount =
    checkedAvailableAmount + deliveryFee - (bestUsableCoupon?.applicableDiscountAmount ?? 0);

  const outOfStockList = basket.items.filter((item) => item.stock.quantity < item.quantity);
  const depletedList = outOfStockList.filter((item) => item.stock.quantity === 0);

  const groupedItems = basket.items.reduce((result, item) => {
    const existGroup = result.find((group) => group.productId === item.productId);
    if (!existGroup) {
      result.push({
        productId: item.productId,
        productName: item.productName,
        items: [],
      });
    }

    const group = existGroup ?? result.at(-1)!;
    group.items.push(item);

    return result;
  }, [] as ItemGroups[]);

  const [modifyItem, setModifyItem] = useState<BasketProductItem | null>(null);
  const [removeItems, setRemoveItems] = useState<BasketProductItem[]>([]);

  useEffect(() => {
    const uncheckList = Object.fromEntries(depletedList.map((item) => [item.key, false]));
    const newChecks = { ...basketChecks, ...uncheckList };
    setBasketChecks(newChecks);
  }, [basket]);

  const handleBuyChecked = () => {
    const itemKeys = checkedItems.map((i) => i.key);
    const orderItemState: OrderItemState = { orderItemKeys: itemKeys };
    setAppliedCoupon(null);
    logAction({
      eventType: 'CLICK',
      eventTarget: 'CART_ORDER',
      eventCategory: 'order',
    });
    navigate('/shop/order', { state: orderItemState });
  };

  const handleBuyItem = (item: BasketProductItem) => {
    const itemKeys = [item.key];
    const orderItemState: OrderItemState = { orderItemKeys: itemKeys };
    navigate('/shop/order', { state: orderItemState });
  };

  const handleRemoved = () => {
    setRemoveItems([]);
  };

  const handleCheck = (item: BasketProductItem, checked: boolean) => {
    setBasketCheck(item.key, checked);
  };

  const handleCheckAll: BareCheckboxProps['onChange'] = (_, checked) => {
    const checkList = Object.fromEntries(basket.items.map(({ key }) => [key, checked]));
    const uncheckList = Object.fromEntries(depletedList.map((item) => [item.key, false]));
    const newChecks = { ...checkList, ...uncheckList };
    setBasketChecks(newChecks);
  };

  if (basket.count === 0) {
    return <EmptyBasket />;
  }
  return (
    <>
      <Page
        type={'page'}
        navigationBarProps={{
          center: (
            <Link
              to={'/shop'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Logo color={'typography.tertiary'} height={'20px'}>
                shop
              </Logo>
            </Link>
          ),
          trail: (
            <Stack height={'20px'} flexDirection={'row'} gap={'12px'}>
              <LikeIconWithCount />
            </Stack>
          ),
        }}
        fixedBottomPosition={'sticky'}
        fixedBottom={
          <Container
            bottom
            py={'8px'}
            px={'20px'}
            sx={() => ({ zIndex: 1 })}
            background={'paper'}
            stackProps={{
              column: true,
              gap: '8px',
            }}
          >
            <Button
              fullWidth
              disabled={totalOrderAvailableAmount <= 0}
              color={'primary'}
              variant={'contained'}
              onClick={handleBuyChecked}
            >
              {
                i18n.t('shop-basket-buyTotalAmount', {
                  totalAvailableAmount: totalOrderAvailableAmount.toLocaleString(),
                  currency,
                }) // Buy {totalAvailableAmount} {currency}
              }
            </Button>
            <Typography variant={'caption'} color={'typography.tertiary'} textAlign={'center'}>
              {
                i18n.t('shop-basket-reviewAndConsent') // I have reviewed and consent to the above order.
              }
            </Typography>
          </Container>
        }
      >
        <Container>
          <DeliveryInstruction />
        </Container>
        <Container px={'20px'} py={'12px'}>
          <Stack row alignItems={'center'} justifyContent={'space-between'}>
            <Checkbox onChange={handleCheckAll} checked={isAllChecked} label={'Select All'} />
            <Stack row alignItems={'center'} justifyContent={'flex-end'}>
              <Button
                disabled={!checkedItems?.length}
                variant={'contained'}
                size={'small'}
                color={'fill'}
                sx={{
                  py: '6px',
                }}
              >
                <Typography
                  color={'typography.secondary'}
                  variant={'FootNote_B'}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  onClick={() => {
                    const checkedItems = groupedItems
                      .map((group) => group.items)
                      .flat(1)
                      .filter((item) => basketChecks[item.key]);
                    setRemoveItems((prev) => [...prev, ...checkedItems]);
                  }}
                >
                  <Icon width={'16px'} color={'typography.tertiary'}>
                    close
                  </Icon>
                  Delete Selected
                </Typography>
              </Button>
            </Stack>
          </Stack>
        </Container>
        <Divider type={'line'} color={'fill'} />
        <Stack column>
          {basket.items.length > 0 &&
            groupedItems
              .map((group) => {
                return group.items.map((item, idx, items) => {
                  const key = item.key;
                  return (
                    <Fragment key={key}>
                      <BasketItem
                        item={item}
                        checked={basketChecks[item.key]}
                        onDelete={(item) => {
                          setRemoveItems((prev) => [...prev, item]);
                        }}
                        onModify={() => {
                          setModifyItem(item);
                        }}
                        onOrder={(item) => {
                          handleBuyItem(item);
                        }}
                        onCheck={handleCheck}
                      />
                      {idx !== items.length - 1 && <Divider type={'line'} color={'fill'} />}
                    </Fragment>
                  );
                });
              })
              .flat(1)}
        </Stack>
        <Divider type={'box'} color={'fill'} />
        <Area
          title={
            i18n.t('shop-basket-paymentDetails') // Payment Details
          }
        >
          <Stack column>
            <Container
              px={'20px'}
              py={'12px'}
              stackProps={{
                row: true,
                justifyContent: 'space-evenly',
              }}
            >
              <Typography
                flex={1}
                textAlign={'left'}
                color={'typography.secondary'}
                whiteSpace={'nowrap'}
              >
                {
                  i18n.t('shop-basket-itemCost') // Item Cost
                }
              </Typography>
              <Container
                stackProps={{
                  display: 'column',
                }}
              >
                <Typography flex={1} variant={'BaseS_B'} textAlign={'right'}>
                  {currency}
                  {checkedAvailableAmount.toLocaleString()}
                </Typography>
                {checkedAmount !== checkedAvailableAmount ? (
                  <Typography
                    strike={checkedAmount !== checkedAvailableAmount}
                    flex={1}
                    lineHeight={1}
                    variant={'FootNote'}
                    textAlign={'right'}
                    color={'typography.tertiary'}
                  >
                    {currency}
                    {checkedAmount.toLocaleString()}
                  </Typography>
                ) : null}
              </Container>
            </Container>

            <Container
              px={'20px'}
              py={'12px'}
              stackProps={{
                row: true,
                justifyContent: 'space-evenly',
              }}
            >
              <Typography flex={1} textAlign={'left'} color={'typography.secondary'}>
                {
                  i18n.t('shop-basket-deliveryFee') // Delivery Fee
                }
              </Typography>
              <Typography variant={'BaseS_B'} textAlign={'right'}>
                {deliveryFee === 0
                  ? i18n.t('shop-basket-free')
                  : `${currency}${deliveryFee.toLocaleString()}`}
              </Typography>
            </Container>
            {bestUsableCoupon && (
              <Container>
                <Container
                  px={'20px'}
                  py={'12px'}
                  stackProps={{
                    column: true,
                    justifyContent: 'space-evenly',
                  }}
                >
                  <Stack row>
                    <Typography flex={1} textAlign={'left'} color={'typography.secondary'}>
                      {i18n.t('common-coupon-discount')}({bestUsableCoupon?.coupon?.name ?? ''})
                    </Typography>
                    <Typography variant={'BaseS_B'} textAlign={'right'}>
                      {bestUsableCoupon?.applicableDiscountAmount
                        ? `- ${currency}${bestUsableCoupon?.applicableDiscountAmount.toLocaleString()}`
                        : ''}
                    </Typography>
                  </Stack>
                  <Typography variant={'FootNote'} color={'text.secondary'}>
                    {i18n.t('shop-basket-choose-while-order')}
                  </Typography>
                </Container>
              </Container>
            )}
          </Stack>
          <Divider type={'line'} color={'fill'} />
          <Container
            py={'12px'}
            px={'20px'}
            stackProps={{
              row: true,
              justifyContent: 'space-evenly',
            }}
          >
            <Typography
              variant={'BaseM_B'}
              flex={1}
              textAlign={'left'}
              color={'typography.secondary'}
            >
              {
                i18n.t('shop-basket-total') // TOTAL
              }
            </Typography>
            <Typography variant={'BaseM_B'} color={'primary'} flex={1} textAlign={'right'}>
              {currency}
              {totalOrderAvailableAmount.toLocaleString()}
            </Typography>
          </Container>
          {issuableCoupons && issuableCoupons.length > 0 && (
            <Container px={'20px'} pt={'12px'} pb={'20px'}>
              <IssuableCouponsButton
                issuableCoupons={issuableCoupons}
                onClick={handleIssueCoupons}
              />
            </Container>
          )}
        </Area>
        <Divider type={'box'} color={'fill'} />
        <RecommendedProducts />
      </Page>

      {modifyItem && (
        <ModifyItemPopover
          open={!!modifyItem}
          onClose={() => setModifyItem(null)}
          item={modifyItem}
        />
      )}
      {removeItems.length > 0 && (
        <RemoveItemsPopover
          open={removeItems.length > 0}
          onClose={() => setRemoveItems([])}
          removeItems={removeItems}
          onRemoved={handleRemoved}
        />
      )}
    </>
  );
}

function EmptyBasket() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const gotoShopping = () => {
    navigate('/shop');
  };

  return (
    <Page
      type={'page'}
      name={'Basket'}
      fixedBottomPosition={'sticky'}
      fixedBottom={
        <Container
          py={'8px'}
          px={'20px'}
          background={'paper'}
          stackProps={{
            column: true,
            gap: '8px',
          }}
        >
          <Button fullWidth color={'primary'} variant={'contained'} onClick={gotoShopping}>
            {
              i18n.t('shop-basket-returnToShopping') // Return to Shopping
            }
          </Button>
        </Container>
      }
    >
      <Spotlight
        size={'large'}
        textAlign={'center'}
        headline={
          i18n.t('shop-basket-cartEmpty') // Oops! Your cart is empty!
        }
        headlineProps={{ color: 'typography.tertiary' }}
      />
      <Divider type={'box'} color={'fill'} />
      <RecommendedProducts />
    </Page>
  );
}

function DeliveryInstruction() {
  const { cutOffRemains, deliveryDayExpression, deliveryHoursKST } = useDeliveryCutOff();
  const { i18n } = useTranslation();
  return (
    <FillBox type={'fill'} color={'primary'} justifyContent={'center'} borderRadius={'0px'}>
      <Typography
        variant={'BaseS_B'}
        textAlign={'center'}
        whiteSpace={'pre'}
        color={'primary.main'}
      >
        {
          i18n.t('shop-product-orderWithinReceiveToday', {
            timeUntilCutOff: cutOffRemains.text,
            deliveryDay: deliveryDayExpression,
            deliveryHour: deliveryHoursKST,
          }) // Order within {deliveryCutOff.timeUntilCutOff.text} to receive Delivery before 11pm Today
        }
      </Typography>
    </FillBox>
  );
}
